// The content identity of a chartbook edition that is MANY IMAGES rather than one PDF.
//
// WHY THIS EXISTS. `yardeni_register_chartbook_edition` takes `_pdf_sha256 text`, refuses an
// empty one ("pdf_sha256 is required (the frozen golden PDF hash)"), and is IDEMPOTENT on
// (edition_label, pdf_sha256). The UI asked an operator to paste 64 hex characters described as
// "the hash of the frozen golden PDF" — an unfollowable instruction for a CIO holding forty
// PNGs, which is what the Yardeni chartbook actually is.
//
// The column name is the only thing here that says PDF. Nothing downstream reads it as one:
// grepped across the repo, `pdf_sha256` is the idempotency key and a 12-character display
// string on the editions list, and nothing parses, fetches or opens it. The table itself
// already models a multi-page artefact — it carries `page_count`, `page_cursor` and
// `stage_cap`, and the read pipeline walks PAGES. So what the field needs is a DETERMINISTIC
// CONTENT IDENTITY for the edition, and a digest over the image set is exactly as good at that
// job as a PDF hash, provided it is computed the same way every time.
//
// THE SPEC, which the shell command in the UI and these functions must agree on byte for byte:
//
//   1. Take every image file in the edition folder, recursively.
//   2. Path = the path RELATIVE TO THE CHOSEN FOLDER, '/' separated, with no leading './'.
//      (A browser directory picker reports `webkitRelativePath` WITH the chosen folder as its
//      first segment; that segment is stripped, so the digest does not change when the folder
//      is renamed. `find . -printf '%P\n'` run inside the folder already produces this form.)
//   3. Sort by that path, by CODE UNIT (a byte/ordinal sort — LC_ALL=C, not locale collation,
//      which would order differently for the same files on a different machine).
//   4. One line per file: `${sha256_lowercase_hex}  ${path}` — TWO spaces, matching
//      `sha256sum` output — each line terminated by '\n', INCLUDING the last.
//   5. The digest is sha256 of that text as UTF-8.
//
// It changes if any image changes, or if one is added, removed or renamed — which is the whole
// point of a frozen edition hash. `tests/investment-reporting/editionManifestHash.test.ts`
// pins the format AND asserts this implementation agrees with the real `sha256sum` pipeline
// for the same input, so the two documented routes cannot drift apart.

/** Image extensions an edition may contain. Lowercased before comparison. */
export const EDITION_IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "webp"] as const;

export interface ManifestEntry {
  /** Path relative to the edition folder, '/' separated. */
  path: string;
  /** Lowercase 64-hex sha256 of the file's bytes. */
  sha256: string;
}

export function isEditionImage(name: string): boolean {
  const dot = name.lastIndexOf(".");
  if (dot < 0) return false;
  const ext = name.slice(dot + 1).toLowerCase();
  return (EDITION_IMAGE_EXTENSIONS as readonly string[]).includes(ext);
}

/**
 * Strip the chosen folder from a directory-picker path.
 *
 * `webkitRelativePath` is "chartbook/page01.png" for a folder called `chartbook`. The digest
 * must not depend on what the operator happened to call the folder, so the first segment goes.
 * A file with no relative path (a plain multi-file picker) falls back to its own name.
 */
export function relativeEditionPath(file: Pick<File, "name"> & { webkitRelativePath?: string }): string {
  const rel = typeof file.webkitRelativePath === "string" ? file.webkitRelativePath : "";
  if (!rel) return file.name;
  const cut = rel.indexOf("/");
  return cut >= 0 ? rel.slice(cut + 1) : rel;
}

/**
 * Build the manifest text. Sorted by path with a CODE-UNIT comparison — deliberately NOT
 * `localeCompare`, which is locale-dependent and would give two machines different digests for
 * the same folder.
 */
export function buildEditionManifest(entries: ManifestEntry[]): string {
  const sorted = [...entries].sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
  // Trailing newline on EVERY line, last one included: `printf '...\n'` per file, which is
  // what the shell pipeline feeds to the outer sha256sum.
  return sorted.map((e) => `${e.sha256}  ${e.path}\n`).join("");
}

function toHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** sha256 of raw bytes, lowercase hex. */
export async function sha256Hex(bytes: ArrayBuffer | Uint8Array): Promise<string> {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  // Copy into a fresh ArrayBuffer so a Uint8Array view over a larger buffer hashes only its
  // own bytes — subtle.digest takes the WHOLE buffer if handed one directly.
  const copy = new Uint8Array(view.byteLength);
  copy.set(view);
  return toHex(await crypto.subtle.digest("SHA-256", copy.buffer));
}

export interface EditionDigest {
  /** The value to put in `pdf_sha256`. */
  digest: string;
  /** The value to put in `page_count`. */
  pageCount: number;
  /** The manifest text the digest was taken over — shown so the operator can audit it. */
  manifest: string;
  entries: ManifestEntry[];
  /** Files the picker returned that are not edition images, so they can be reported, not hidden. */
  skipped: string[];
}

/**
 * Hash a set of files IN THE BROWSER. Nothing is uploaded — the bytes are read locally and only
 * the digest leaves the page. That matters for a licensed third-party chartbook: registering an
 * edition is an attestation that these exact bytes are the edition, not a request to store them.
 */
export async function editionDigestFromFiles(
  files: ArrayLike<File>,
  readBytes: (f: File) => Promise<ArrayBuffer> = (f) => f.arrayBuffer(),
): Promise<EditionDigest> {
  const all = Array.from(files);
  const images = all.filter((f) => isEditionImage(f.name));
  const skipped = all.filter((f) => !isEditionImage(f.name)).map((f) => relativeEditionPath(f));

  const entries: ManifestEntry[] = [];
  for (const f of images) {
    entries.push({ path: relativeEditionPath(f), sha256: await sha256Hex(await readBytes(f)) });
  }
  const manifest = buildEditionManifest(entries);
  const digest = await sha256Hex(new TextEncoder().encode(manifest));
  return { digest, pageCount: entries.length, manifest, entries, skipped };
}
